import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import { Button } from '../../ui';
import { TextInput } from '../../ui/input/text_input/text_input';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from '../login_register/AuthenticationUILayout';
import { CompanyLogoBadge } from '../login_register/CompanyLogoBadge';
import { validate, validateForm } from '../login_register/validation/validation_rules';
import { acceptInvite, getInvite, InviteDetails } from '../../utils/api_service/invite_api';
import { AnimatedLoginSequence } from '../../assets/svgs/AnimateLogin';
import { apiUrl } from '../../utils/api_service/api_url';
import { finishSignIn } from '../../utils/auth/finishSignIn';
import { setPending } from '../../utils/auth/mfaHandoff';
import { ProviderIcon } from '../login_register/ProviderIcon';

type Stage = 'loading' | 'ready' | 'invalid';

export const InvitePage = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>('loading');
  const [details, setDetails] = useState<InviteDetails | null>(null);
  const [form, setForm] = useState<FormInput>({
    email: '',
    password: '',
    confirmPassword: '',
    errors: { emailError: '', passwordError: '', confirmPasswordError: '' },
  });

  useEffect(() => {
    getInvite(token)
      .then((res) => res.json())
      .then((data: InviteDetails) => {
        if (data && data.success) {
          setDetails(data);
          setForm((f) => ({ ...f, email: data.email }));
          setStage('ready');
        } else {
          setStage('invalid');
        }
      })
      .catch(() => setStage('invalid'));
  }, [token]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const errors = validate(event.target, form);
    setForm({ ...form, [event.target.name]: event.target.value, errors });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm(form)) {
      toast.error(t(AUTH_KEYS.login.invalidDetails), { position: 'bottom-center' });
      return;
    }
    try {
      const res = await acceptInvite(token, form.password);
      const data = await res.json();
      if (!res.ok || !data.success) {
        setStage('invalid');
        toast.error(t(AUTH_KEYS.login.invalidDetails), { position: 'bottom-center' });
        return;
      }
      // The invitation's deep-link (e.g. the specific assessment) wins,
      // otherwise route by role like a normal sign-in.
      const next = data.nextPath || details?.nextPath || '';
      // Same MFA gates as password login: an enrolled authenticator is
      // challenged, a tenant policy forces enrolment, before any session.
      if (data.mfaRequired && data.challengeToken) {
        setPending('challenge', data.challengeToken, next);
        history.push('/auth/mfa');
        return;
      }
      if (data.mfaEnrollmentRequired && data.enrollToken) {
        setPending('enroll', data.enrollToken, next);
        history.push('/auth/mfa/enroll');
        return;
      }
      // One seam for "signed in".
      history.push(await finishSignIn(data, { deepLink: next }));
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong', { position: 'bottom-center' });
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout leftContent={<AnimatedLoginSequence />}>
      <div className='flex items-start flex-col w-full gap-6 pt-10'>
        <CompanyLogoBadge logoUrl={companyLogo} companyName={companyName} />

        {stage === 'loading' && <p className='text-slate-500'>Loading your invitation…</p>}

        {stage === 'invalid' && (
          <div className='flex flex-col gap-3'>
            <h2 className='text-xl font-semibold text-slate-900'>This invitation link is no longer valid</h2>
            <p className='text-sm text-slate-500'>
              It may have already been used or expired. Ask your administrator to send a new invitation.
            </p>
            <Button onClick={() => history.push('/login')} label='Go to sign in' />
          </div>
        )}

        {stage === 'ready' && details && (
          <form className='flex flex-col w-full gap-4' onSubmit={handleSubmit}>
            <div>
              <h2 className='text-2xl font-semibold text-slate-900'>
                Welcome{details.firstName ? `, ${details.firstName}` : ''}
              </h2>
              <p className='text-sm text-slate-500'>
                Set a password to join {details.organizationName || 'your organization'}.
              </p>
            </div>
            <TextInput
                type='email'
                label={t(AUTH_KEYS.register.email)}
                name='email'
                value={form.email}
                disabled
                onChange={handleChange}
            />
            <TextInput
                type='password'
                label={t(AUTH_KEYS.register.password)}
                name='password'
                value={form.password}
                onChange={handleChange}
            />
            {form.errors.passwordError && (
              <span className='text-xs text-red-500 -mt-2'>{form.errors.passwordError}</span>
            )}
            <TextInput
                type='password'
                label={t(AUTH_KEYS.register.confirmPassword)}
                name='confirmPassword'
                value={form.confirmPassword || ''}
                onChange={handleChange}
            />
            {form.errors.confirmPasswordError && (
              <span className='text-xs text-red-500 -mt-2'>{form.errors.confirmPasswordError}</span>
            )}
            <Button type='submit' label='Create account' />
          </form>
        )}

        {stage === 'ready' && (details?.methods?.ssoOptions?.length || 0) > 0 && (
          <div className='flex flex-col gap-2'>
            {details!.methods.ssoOptions!.map((opt) => (
              <button
                  key={opt.provider}
                  type='button'
                  onClick={() => {
                    const returnTo = encodeURIComponent(window.location.origin);
                    window.location.href = `${apiUrl}/auth/sso/start?returnTo=${returnTo}&provider=${encodeURIComponent(opt.provider)}&invite=${encodeURIComponent(token)}`;
                  }}
                  className='w-full py-3 px-6 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-sm transition-all flex items-center justify-center gap-2'
              >
                <ProviderIcon provider={opt.provider} />
                {opt.label || 'Continue with SSO'}
              </button>
            ))}
          </div>
        )}
      </div>
    </AuthenticationUiLayout>
  );
};
